import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { InspectionDeviationResultDto } from "@/types";
import Accordion from "@/components/organisms/AccordionDrawer";
import Divider from "@/components/atoms/Divider";
import { useInspectionFormat } from "@/components/organisms/InspectionModal/useInspectionFormat";
import { UnitNumberInput } from "@/components/organisms/InspectionModal/components/FormParts";
import {
  DeviationDecision,
  DeviationHeader,
  InspectionPhotos,
  Row,
} from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab/TabParts";
import { DecisionCost, DecisionFields, fieldError } from "./DecisionParts";
import { DecisionPreview } from "./useDecisionPreview";
import { DeviationDecisionsForm, isPendingDeviation, registeredAmount } from "./useDeviationDecisionsForm";

interface DeviationProductDecisionsProps {
  /** The inspection's deviations of kind PRODUCT, pending or decided. */
  deviations: InspectionDeviationResultDto[];
  form: DeviationDecisionsForm;
  preview: DecisionPreview;
}

/**
 * The deviation products to decide: confirmed, the weight (corrected if need be) is taken off the order at the
 * product's price; rejected, it isn't invoiced. Decided ones are shown as in the order drawer.
 */
export default function DeviationProductDecisions({ deviations, form, preview }: DeviationProductDecisionsProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();

  return (
    <Accordion initiallyOpen title={t("inspections:deviations.productsTitle")}>
      {deviations.map((deviation, index) => {
        const values = form.valuesOf(deviation);
        const isPending = isPendingDeviation(deviation);
        return (
          <Fragment key={deviation.id}>
            {index > 0 && <Divider margin={false} />}
            <Grid p={20} data-testid="deviation-decision-product">
              <DeviationHeader deviation={deviation} />
              <Row label={t("inspections:drawer.deviationWeight")}>
                {/* Confirming can correct the weight, starting from the inspector's. */}
                {isPending && values.decision === "CONFIRMED" ? (
                  <UnitNumberInput
                    aria-label={t("inspections:drawer.deviationWeight")}
                    unit="kg"
                    allowDecimal={false}
                    maw={200}
                    value={values.amount}
                    onChange={(amount) => form.update(deviation, { amount })}
                    error={fieldError(form.errors, deviation, "amount", t)}
                    data-testid={`deviation-decision-${deviation.id}-amount`}
                  />
                ) : (
                  `${format.number(registeredAmount(deviation))} kg`
                )}
              </Row>
              <Row label={t("inspections:drawer.unitPrice")}>
                {format.unitPrice(
                  deviation.unitPrice,
                  deviation.currencyType,
                  format.deviationUnit(deviation.unitCode)
                )}
              </Row>
              <Row label={t("inspections:deviations.cost")}>
                <DecisionCost deviation={deviation} preview={preview} />
              </Row>
              <Row label={t("inspections:photos.label")}>
                <InspectionPhotos photoIds={deviation.photoIds} />
              </Row>
              {isPending ? (
                <DecisionFields
                  deviation={deviation}
                  values={values}
                  onChange={(patch) => form.update(deviation, patch)}
                  errors={form.errors}
                />
              ) : (
                <DeviationDecision deviation={deviation} isBuyer />
              )}
            </Grid>
          </Fragment>
        );
      })}
    </Accordion>
  );
}
